/**
 * Provider component for notification realtime subscriptions
 * Ensures a single subscription exists for the authenticated user.
 * 
 * FMEA Mitigation F2: Prevents duplicate subscriptions by centralizing
 * the subscription in a single provider component.
 */

import { createContext, useContext, ReactNode } from 'react';
import { useMyProfile } from '@/hooks/profile';
import { useNotificationRealtimeSubscription } from '@/hooks/notifications/useNotificationRealtimeSubscription';

interface NotificationRealtimeContextValue {
  isConnected: boolean;
}

const NotificationRealtimeContext = createContext<NotificationRealtimeContextValue>({
  isConnected: false,
});

export function useNotificationRealtime() {
  return useContext(NotificationRealtimeContext);
}

interface NotificationRealtimeProviderProps {
  children: ReactNode;
}

export function NotificationRealtimeProvider({ children }: NotificationRealtimeProviderProps) {
  const { data: profile } = useMyProfile();
  const profileId = profile?.id;

  const { isConnected } = useNotificationRealtimeSubscription({
    profileId,
    enabled: !!profileId,
  });

  return (
    <NotificationRealtimeContext.Provider value={{ isConnected }}>
      {children}
    </NotificationRealtimeContext.Provider>
  );
}
